<template>
  <wui-card
    class="w-[280px] phone:w-[240px]"
    state="text"
    shadow="overflow"
    data-test="guest-item-dropdown"
    @click.stop
  >
    <wui-list>
      <wui-list-item
        v-if="isGuestOnline"
        :icon="callIcon"
        :disabled="!registered || isCalling"
        data-test="guest-menu-call"
        @click="onCall"
      >
        <p>{{ $t('common.makeCall') }}</p>
      </wui-list-item>

      <hr
        v-if="isGuestOnline"
        class="mx-1 my-1 border-t-bor-nav"
      >

      <wui-list-item
        icon="clear"
        variant="negative"
        :disabled="isRemoving"
        data-test="remove-member"
        @click="removeFromRoom"
      >
        <p>{{ $t('chat.removeFromGroup') }}</p>
      </wui-list-item>
    </wui-list>
  </wui-card>
</template>

<script setup lang="ts">
import {
  useRenderNotifier,
  WuiCard,
  WuiList,
  WuiListItem,
} from '@wui/common-library'
import { computed, PropType, ref } from 'vue'

import { useAutoMarkAsReadIfExecutor } from '@/entities/chat-handlers'
import { Guest } from '@/entities/conference-participant'
import { chatModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'
import { useWebRTC } from '@/entities/webrtc'

import { useLocalization } from '@/shared/composables/localization'
import { useStoreGuestStatus } from '@/shared/guest-status'

import { call, remove } from '../lib/guest-item-dropdown'

const props = defineProps({
  guest: {
    type: Object as PropType<Guest>,
    required: true,
  },
  roomId: {
    type: String,
    required: true,
  },
  roomPServed: {
    type: String,
    required: true,
  },
})

const emit = defineEmits<{
  (event: 'hideMenu'): void
}>()

const { registered } = useWebRTC()
const { getStatusByPServed } = useStoreGuestStatus()
const isGuestOnline = computed(() => getStatusByPServed(props.guest.pServed)?.isOnline === true)
const storeRooms = useStoreRooms()
const { notifyError, notifySuccess } = useRenderNotifier()
const { t } = useLocalization()
const autoMarkAsReadIfExecutor = useAutoMarkAsReadIfExecutor()

const isRemoving = ref(false)
const isCalling = ref(false)
const callIcon = computed(() => {
  if (isCalling.value) {
    return 'spinner'
  }
  return registered.value ? 'phoneM' : 'callDisabled'
})

const onCall = () => {
  if (!registered.value || isCalling.value) {
    return
  }

  isCalling.value = true
  try {
    call(props.roomPServed, props.guest.pServed)
    emit('hideMenu')
  } finally {
    isCalling.value = false
  }
}

const removeFromRoom = () => {
  isRemoving.value = true
  remove(props.roomPServed, props.guest.pServed)
    .then((data) => {
      if (data) {
        storeRooms.removeGuest(props.roomId, props.guest.pServed)
        chatModel.useChatMessageStore().addMessage(props.roomPServed, data)
        autoMarkAsReadIfExecutor(data, props.roomPServed)
        notifySuccess(t('guest.guestNameRemovedFromGroup', { name: props.guest.name }))
        emit('hideMenu')
      } else {
        notifyError(t('guest.failedToRemoveGuestFromRoom', { name: props.guest.name }))
      }
    }).catch(() => {
      notifyError(t('guest.failedToRemoveGuestFromRoom', { name: props.guest.name }))
    }).finally(() => {
      isRemoving.value = false
    })
}
</script>
